import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const COLORS = {
  bg: '#FFFFFF',
  bgAlt: '#F8FAFC',
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  borderHover: '#CBD5E1',
  indigo: '#6366F1',
  violet: '#8B5CF6',
  fuchsia: '#D946EF',
  emerald: '#10B981',
  rose: '#F43F5E'
};

const ChartIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="chartGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="50%" stopColor={COLORS.violet} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <rect x="3" y="13" width="3.5" height="8" rx="1" fill="url(#chartGrad)" />
    <rect x="10.25" y="8" width="3.5" height="13" rx="1" fill="url(#chartGrad)" />
    <rect x="17.5" y="3" width="3.5" height="18" rx="1" fill="url(#chartGrad)" />
  </svg>
);

const PhoneIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="phoneGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="50%" stopColor={COLORS.violet} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path
      d="M5.5 4.5C5.5 3.67 6.17 3 7 3h2.2c.62 0 1.17.38 1.39.96l1.07 2.85a1.5 1.5 0 01-.42 1.66l-1.3 1.07a13 13 0 005.52 5.52l1.07-1.3a1.5 1.5 0 011.66-.42l2.85 1.07c.58.22.96.77.96 1.39V17c0 .83-.67 1.5-1.5 1.5h-1C10.97 18.5 5.5 13.03 5.5 6.5v-2z"
      fill="url(#phoneGrad)"
    />
  </svg>
);

const GavelIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="gavelGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="50%" stopColor={COLORS.violet} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path d="M12 3l4 1-2 7-4-1 2-7z" fill="url(#gavelGrad)" />
    <path d="M9.5 11l4 1-1.2 4.2-4-1L9.5 11z" fill="url(#gavelGrad)" opacity="0.85" />
    <rect x="4" y="19" width="16" height="2" rx="1" fill="url(#gavelGrad)" />
    <rect
      x="9.5"
      y="14.2"
      width="3"
      height="6"
      rx="1"
      transform="rotate(-20 11 17)"
      fill="url(#gavelGrad)"
      opacity="0.6"
    />
  </svg>
);

const BrainIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="brainGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="50%" stopColor={COLORS.violet} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path d="M12 2C8.5 2 6 4.5 6 7.5C6 8.5 6.3 9.5 6.8 10.3C5.7 11 5 12.2 5 13.5C5 15.4 6.3 17 8 17.5V20C8 21.1 8.9 22 10 22H14C15.1 22 16 21.1 16 20V17.5C17.7 17 19 15.4 19 13.5C19 12.2 18.3 11 17.2 10.3C17.7 9.5 18 8.5 18 7.5C18 4.5 15.5 2 12 2Z" stroke="url(#brainGrad)" strokeWidth="1.8" fill="none"/>
    <path d="M12 2V22M8 8H16M7 13H17" stroke="url(#brainGrad)" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const TrendingIcon = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <defs>
      <linearGradient id="trendGrad" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="50%" stopColor={COLORS.violet} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
    <path d="M3 17L8 12L12 16L21 7" stroke="url(#trendGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 7H21V11" stroke="url(#trendGrad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Arrow = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M5 12h14m0 0l-5-5m5 5l-5 5"
      stroke="url(#arrowGrad)"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <defs>
      <linearGradient id="arrowGrad" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor={COLORS.indigo} />
        <stop offset="100%" stopColor={COLORS.fuchsia} />
      </linearGradient>
    </defs>
  </svg>
);

const projects = [
  {
    Icon: ChartIcon,
    title: 'Skip the Survey. Predict the Market.',
    problem:
      'Survey research takes weeks. Response rates are collapsing. By the time data arrives, the window has closed — and you still only hear from the 2% who bothered to respond.',
    solution:
      'AI models that predict full-population survey outcomes from small samples. Psychographic profiling that captures what respondents would have said, not just what the loudest ones did say. Deployed with a top-10 research agency.'
  },
  {
    Icon: PhoneIcon,
    title: 'Filing Reports by Voice.',
    problem:
      'Field technicians, construction crews, and frontline workers need to file reports. But typing on a phone in the rain while wearing gloves is miserable. Voice is natural; forms are not.',
    solution:
      'Workers call a number and speak naturally. The AI structures their input into clean, filed reports — no app to install, no form to fill out. Live at reportbyphone.com.'
  },
  {
    Icon: GavelIcon,
    title: 'Live Courtroom Intelligence.',
    problem:
      'Defense lawyers walk into court against prosecutors with databases, case law search, and institutional memory. The defense has a yellow pad and two hours of prep the night before.',
    solution:
      'Courtroom AI that delivers live case analysis, precedent research, and strategic recommendations during proceedings. The playing field is level now.'
  },
  {
    Icon: BrainIcon,
    title: 'Live Persuasion Feedback.',
    problem:
      'Sales teams, negotiators, and executives need to get better at high-stakes conversations. Role-play practice exists, but nobody gets honest, real-time feedback on their actual persuasion technique.',
    solution:
      'An AI coach that tracks belief-state changes during live conversation. Identifies which arguments landed, which fell flat, and what to try next — grounded in proven persuasion principles, not generic advice.'
  },
  {
    Icon: TrendingIcon,
    title: 'Move Before the Market Does.',
    problem:
      'Prediction markets move fast. Human traders can monitor a few markets; they cannot monitor hundreds simultaneously while synthesizing news, sentiment shifts, and structural pricing anomalies in real time.',
    solution:
      'Autonomous AI agents that synthesize signals across prediction markets and execute strategies at machine speed. Built for clients who need to exploit opportunities before human traders even see them.'
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  }
};

const Projects = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [expandedCard, setExpandedCard] = useState(null);

  return (
    <section
      id="projects"
      ref={ref}
      style={{
        backgroundColor: COLORS.bg,
        color: COLORS.textPrimary,
        padding: 'var(--spacing-8xl) 0'
      }}
    >
      <div className="container">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          style={{ textAlign: 'center', marginBottom: 'var(--spacing-4xl)' }}
        >
          <motion.div variants={itemVariants}>
            <span
              style={{
                display: 'inline-block',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--text-sm)',
                letterSpacing: 'var(--tracking-widest)',
                textTransform: 'uppercase',
                background: `linear-gradient(135deg, ${COLORS.indigo}, ${COLORS.violet}, ${COLORS.fuchsia})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                marginBottom: 'var(--spacing-lg)'
              }}
            >
              Projects
            </span>
          </motion.div>

          <motion.h2
            variants={itemVariants}
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              fontWeight: 700,
              color: COLORS.textPrimary,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              marginBottom: 'var(--spacing-lg)'
            }}
          >
            What We're Working On
          </motion.h2>

          <motion.p
            variants={itemVariants}
            style={{
              fontSize: 'var(--text-lg)',
              color: COLORS.textSecondary,
              maxWidth: '640px',
              margin: '0 auto',
              lineHeight: 1.6
            }}
          >
            Hard problems. Real systems. No slides.
          </motion.p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? 'visible' : 'hidden'}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 'var(--spacing-xl)'
          }}
        >
          {projects.map(({ Icon, title, problem, solution }) => {
            const isExpanded = expandedCard === title;
            return (
            <motion.article
              key={title}
              variants={itemVariants}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              style={{
                backgroundColor: COLORS.bg,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 'var(--radius-xl)',
                padding: 'var(--spacing-2xl)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-lg)',
                boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
                transition: 'box-shadow 250ms ease, border-color 250ms ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow =
                  '0 18px 40px -12px rgba(99, 102, 241, 0.18), 0 4px 12px rgba(15, 23, 42, 0.06)';
                e.currentTarget.style.borderColor = COLORS.borderHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 1px 2px rgba(15, 23, 42, 0.04)';
                e.currentTarget.style.borderColor = COLORS.border;
              }}
            >
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: 'var(--radius-lg)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: COLORS.bgAlt,
                  border: `1px solid ${COLORS.border}`
                }}
              >
                <Icon size={28} />
              </div>

              <h3
                style={{
                  fontSize: 'var(--text-2xl)',
                  fontWeight: 700,
                  color: COLORS.textPrimary,
                  letterSpacing: '-0.02em',
                  margin: 0
                }}
              >
                {title}
              </h3>

              <div
                style={{
                  borderLeft: `3px solid ${COLORS.rose}`,
                  paddingLeft: 'var(--spacing-md)'
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: COLORS.rose,
                    marginBottom: '6px',
                    fontWeight: 600
                  }}
                >
                  Problem
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: COLORS.textSecondary,
                    lineHeight: 1.6,
                    margin: 0
                  }}
                >
                  {problem}
                </p>
              </div>

              <button
                type="button"
                className="project-solution-toggle"
                aria-expanded={isExpanded}
                onClick={() =>
                  setExpandedCard(isExpanded ? null : title)
                }
                style={{
                  alignSelf: 'flex-start',
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 600,
                  color: COLORS.indigo,
                  fontFamily: 'inherit'
                }}
              >
                {isExpanded ? 'Collapse ↑' : 'How we solved it →'}
              </button>

              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.5, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="project-arrow"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '24px'
                }}
              >
                <Arrow />
              </motion.div>

              <div
                className={`project-solution${isExpanded ? ' expanded' : ''}`}
                style={{
                  borderLeft: `3px solid ${COLORS.emerald}`,
                  paddingLeft: 'var(--spacing-md)'
                }}
              >
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: '0.7rem',
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: COLORS.emerald,
                    marginBottom: '6px',
                    fontWeight: 600
                  }}
                >
                  Solution
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-base)',
                    color: COLORS.textPrimary,
                    lineHeight: 1.6,
                    margin: 0,
                    fontWeight: 500
                  }}
                >
                  {solution}
                </p>
              </div>
            </motion.article>
            );
          })}
        </motion.div>
      </div>

      <style>{`
        .project-solution-toggle { display: none; }
        @media (max-width: 767px) {
          .project-solution-toggle { display: inline-block; }
          .project-arrow { display: none !important; }
          .project-solution {
            max-height: 0;
            overflow: hidden;
            opacity: 0;
            margin: 0;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            border-left-width: 0 !important;
            transition: max-height 320ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms ease, padding 200ms ease;
          }
          .project-solution.expanded {
            max-height: 600px;
            opacity: 1;
            padding-left: var(--spacing-md) !important;
            border-left-width: 3px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default Projects;
